import React, { useState, useEffect } from "react";
import TabsBar from "./notify_components/TabsBar";
import DraftList from "./notify_components/DraftList";
import SentList from "./notify_components/SentList";
import PreferencesView from "./notify_components/PreferencesView";
import EditDraftModal from "./notify_components/EditDraftModal";
import CreateNotificationFormModal from "./notify_components/CreateNotificationFormModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDrafts,
  fetchSentNotifications,
  fetchNotificationPreferences,
  sendNotification,
  deleteDraft,
  addCustomNotification,
  editNotification,
} from "../../redux/slices/notificationsSlice";
import { PlusCircleIcon } from "../../utils/svgIcons";
import { Button, Pagination, Select, SelectItem } from "@heroui/react";

export default function NotificationsAdmin() {
  const dispatch = useDispatch();
  const { drafts, sent, preferences, draftsPagination, sentPagination, status } = useSelector((s) => s.notifications);
  const [activeTab, setActiveTab] = useState("all");
  const [expandedSentItem, setExpandedSentItem] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [isCreateTypeModalOpen, setIsCreateTypeModalOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination states
  const [draftsPage, setDraftsPage] = useState(1);
  const [draftsLimit, setDraftsLimit] = useState(10);

  const [sentPage, setSentPage] = useState(1);
  const [sentLimit, setSentLimit] = useState(10);

  const PAGE_LIMITS = [
    { value: "10", label: "10", key: "10" },
    { value: "20", label: "20", key: "20" },
    { value: "50", label: "50", key: "50" },
    { value: "100", label: "100", key: "100" },
  ];

  // Track currently sending notification IDs
  const [sendingIds, setSendingIds] = useState({});

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch drafts helper
  const fetchCurrentDrafts = (searchVal = debouncedSearch, page = draftsPage, limit = draftsLimit) => {
    const params = {
      page,
      limit,
    };
    if (searchVal) params.search = searchVal;

    if (activeTab === "perfect") {
      params.review_needed = "false";
    } else if (activeTab === "review_needed") {
      params.review_needed = "true";
    }

    dispatch(fetchDrafts(params));
  };

  // Fetch sent helper
  const fetchCurrentSent = (searchVal = debouncedSearch, page = sentPage, limit = sentLimit) => {
    const params = {
      page,
      limit,
    };
    if (searchVal) params.search = searchVal;

    dispatch(fetchSentNotifications(params));
  };

  useEffect(() => {
    fetchCurrentDrafts(debouncedSearch, draftsPage, draftsLimit);
  }, [dispatch, debouncedSearch, activeTab, draftsPage, draftsLimit]);

  useEffect(() => {
    fetchCurrentSent(debouncedSearch, sentPage, sentLimit);
  }, [dispatch, debouncedSearch, sentPage, sentLimit]);

  useEffect(() => {
    dispatch(fetchNotificationPreferences());
  }, [dispatch]);

  // Reset drafts page when switching sub-tabs
  useEffect(() => {
    if (["all", "perfect", "review_needed"].includes(activeTab)) {
      setDraftsPage(1);
    }
  }, [activeTab]);

  // Reset page when search changes
  useEffect(() => {
    setDraftsPage(1);
    setSentPage(1);
  }, [debouncedSearch]);

  // --- Handlers ---
  const handleEditClick = (draft) => {
    setEditingDraft(draft);
    setIsEditModalOpen(true);
  };

  const handleUpdateDraft = async (updatedDraft) => {
    try {
      await dispatch(
        editNotification({
          id: updatedDraft._id || updatedDraft.id,
          data: {
            title: updatedDraft.title,
            description: updatedDraft.description,
            link: updatedDraft.url,
          },
        })
      ).unwrap();

      fetchCurrentDrafts();
    } catch (err) {
      console.error("Edit failed:", err);
    } finally {
      setIsEditModalOpen(false);
    }
  };

  const handleDelete = async (id) => {
    await dispatch(deleteDraft(id));
    fetchCurrentDrafts();
  };

  const handleSend = async (id) => {
    if (sendingIds[id]) return;
    setSendingIds((prev) => ({ ...prev, [id]: true }));
    try {
      await dispatch(sendNotification(id)).unwrap();
      fetchCurrentDrafts();
      fetchCurrentSent();
    } catch (err) {
      console.error("Send failed:", err);
    } finally {
      setSendingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleSendAllPerfect = async () => {
    const perfect = drafts.filter((d) => (d.matchStatus || (d.review_needed === false ? "perfect" : "review_needed")) === "perfect");
    if (!perfect.length) return;

    const ids = perfect.map((d) => d.id || d._id);
    setSendingIds((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = true;
      });
      return next;
    });

    try {
      await Promise.all(ids.map((id) => dispatch(sendNotification(id)).unwrap()));
      fetchCurrentDrafts();
      fetchCurrentSent();
    } catch (err) {
      console.error("Send all perfect failed:", err);
    } finally {
      setSendingIds((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }
  };

  const handleCreateDraft = async (type, formData) => {
    const { title, description, ...details } = formData;
    const finalUrl = details.link || "https://evindia.online";
    const typeMap = {
      ev_news: 1,
      new_launches: 2,
      offers_discounts: 3,
      upcoming_vehicles: 4,
    };
    const isFile = details.image && typeof details.image === "object" && details.image.name;
    const payload = isFile ? new FormData() : {};
    if (isFile) {
      payload.append("title", title || "Untitled Notification");
      payload.append("description", description || "");
      payload.append("link", finalUrl);
      payload.append("alert_type", typeMap[type]);
      payload.append("isCustom", true);
      payload.append("image", details.image);
    } else {
      Object.assign(payload, {
        title: title || "Untitled Notification",
        description,
        link: finalUrl,
        alert_type: typeMap[type],
        isCustom: true,
        image: details.image,
      });
    }
    try {
      await dispatch(addCustomNotification(payload)).unwrap();
      fetchCurrentDrafts();
      fetchCurrentSent();
    } catch (e) {
      console.error(e);
    } finally {
      setIsCreateTypeModalOpen(false);
    }
  };

  return (
    <div className="font-sans text-gray-800  h-full w-full">
      <div className="mx-auto py-4 md:py-8 px-6 overflow-y-auto">
        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Notification Approval Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Review, manage, and create notifications based on user preferences.
            </p>
          </div>

          <Button
            color="warning"
            className="bg-button text-white rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg"
            onPress={() => setIsCreateTypeModalOpen(true)}
            size="lg"
            startContent={<PlusCircleIcon />}
          >
            Create Custom Notification
          </Button>
        </header>

        {/* Tabs & Search */}
        <TabsBar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          drafts={drafts}
          sentItems={sent}
          search={search}
          setSearch={setSearch}
        />

        {/* Content */}
        <div className="space-y-6">
          {["all", "perfect", "review_needed"].includes(activeTab) && (
            <>
              <DraftList
                activeTab={activeTab}
                drafts={drafts}
                onEdit={handleEditClick}
                onDelete={handleDelete}
                onSend={handleSend}
                onSendAllPerfect={handleSendAllPerfect}
                sendingIds={sendingIds}
              />
              {draftsPagination && drafts.length > 0 && (
                <div className="pagination-wrapper w-full flex items-center justify-between mt-4">
                  <div className="flex items-center justify-center w-[200px] gap-1">
                    <p>Show</p>
                    <Select
                      className="max-w-[90px] border-gray-100 rounded-[13px]"
                      placeholder="Page size"
                      variant="bordered"
                      selectedKeys={[String(draftsLimit)]}
                      onSelectionChange={(keys) => {
                        const newLimit = Number(Array.from(keys)[0]);
                        setDraftsLimit(newLimit);
                        setDraftsPage(1);
                      }}
                    >
                      {PAGE_LIMITS.map((item) => (
                        <SelectItem key={item.key} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </Select>
                    <p>Entries</p>
                  </div>
                  <div className="flex justify-center mt-2">
                    <Pagination
                      key={draftsPage}
                      color="success"
                      classNames={{
                        wrapper: "",
                        item: "",
                        cursor: "bg-success text-white",
                      }}
                      size="md"
                      isCompact
                      showControls
                      total={draftsPagination.totalPages || 1}
                      page={draftsPage}
                      onChange={(p) => setDraftsPage(p)}
                    />
                  </div>
                </div>
              )}
            </>
          )}
          {activeTab === "sent" && (
            <>
              <SentList items={sent} expanded={expandedSentItem} setExpanded={setExpandedSentItem} />
              {sentPagination && sent.length > 0 && (
                <div className="pagination-wrapper w-full flex items-center justify-between mt-4">
                  <div className="flex items-center justify-center w-[200px] gap-1">
                    <p>Show</p>
                    <Select
                      className="max-w-[90px] border-gray-100 rounded-[13px]"
                      placeholder="Page size"
                      variant="bordered"
                      selectedKeys={[String(sentLimit)]}
                      onSelectionChange={(keys) => {
                        const newLimit = Number(Array.from(keys)[0]);
                        setSentLimit(newLimit);
                        setSentPage(1);
                      }}
                    >
                      {PAGE_LIMITS.map((item) => (
                        <SelectItem key={item.key} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </Select>
                    <p>Entries</p>
                  </div>
                  <div className="flex justify-center mt-2">
                    <Pagination
                      key={sentPage}
                      color="success"
                      classNames={{
                        wrapper: "",
                        item: "",
                        cursor: "bg-success text-white",
                      }}
                      size="md"
                      isCompact
                      showControls
                      total={sentPagination.totalPages || 1}
                      page={sentPage}
                      onChange={(p) => setSentPage(p)}
                    />
                  </div>
                </div>
              )}
            </>
          )}
          {activeTab === "preferences" && <PreferencesView data={preferences} />}
        </div>
      </div>

      {/* Modals */}
      {isEditModalOpen && (
        <EditDraftModal draft={editingDraft} onSave={handleUpdateDraft} onClose={() => setIsEditModalOpen(false)} />
      )}
      {isCreateTypeModalOpen && (
        <CreateNotificationFormModal
          onSave={handleCreateDraft}
          onClose={() => setIsCreateTypeModalOpen(false)}
        />
      )}
    </div>
  );
}
